import { BackLink } from '@avanta/shared/components/BackLink';
import { PasswordRequirement } from '@avanta/shared/components/PasswordRequirement';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { clearRecoveryCookies } from '@avanta/shared/utils/auth';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'constants/routes';
import { formValidation, initialFormData } from './model';
import { type FormProps, type InitialFormData } from './types';

export const PasswordChange = ({
  onSubmit,
  isLoading,
  showBackLink = true,
}: FormProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.AUTH);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<InitialFormData>({
    defaultValues: initialFormData,
  });

  const password = watch('password');

  const handleOnSubmit = async (data: InitialFormData): Promise<void> => {
    await onSubmit(data.password, data.confirmPassword);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2">
        <Input
          label={t('RecoverPassword.PasswordChange.newPassword.label')}
          type="password"
          placeholder={t('RecoverPassword.PasswordChange.newPassword.placeholder')}
          showPassword
          {...register('password', formValidation.password)}
          autoComplete="new-password"
        />
        <PasswordRequirement password={password} />
      </div>
      <Input
        label={t('RecoverPassword.PasswordChange.confirmPassword.label')}
        type="password"
        placeholder={t('RecoverPassword.PasswordChange.confirmPassword.placeholder')}
        showPassword
        variant={!!errors.confirmPassword ? 'invalid' : 'filled'}
        helperText={errors.confirmPassword?.message ? t(errors.confirmPassword.message) : undefined}
        {...register('confirmPassword', formValidation.confirmPassword)}
        autoComplete="new-password"
      />
      <Button type="submit" isLoading={isLoading}>
        {t('RecoverPassword.PasswordChange.cta')}
      </Button>
      {showBackLink && (
        <BackLink
          label="RecoverPassword.backToLogin"
          i18nNs={LOCIZE_NS.AUTH}
          to={ROUTES.LOGIN}
          onClick={clearRecoveryCookies}
        />
      )}
    </form>
  );
};
